import React, { useCallback } from 'react';
import { UserRoundSearch } from 'lucide-react';
import { Avatar as UiAvatar, AvatarFallback } from '@/components/ui/avatar';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Empty,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import { FieldGroup } from '@/components/ui/field';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/utilities/cn';
import type { Emotion } from '@airhead-games/shared/configs';
import type { RoomPlayer } from '@airhead-games/shared/types';
import GameAvatar from '../avatars/Avatar';
import { useAnimalStyleAvatars } from '../avatars/hooks';
import PlayerSelectorRow from './PlayerSelectorRow';

export type PlayerSelectorOption = {
  id: string;
  title: React.ReactNode;
  description?: React.ReactNode;
  avatar: React.ReactNode;
};

type PlayerSelectorCommonProps = {
  players: RoomPlayer[];
  columns?: number;
  leadingOptions?: PlayerSelectorOption[];
  selectedEmotion?: Emotion;
  className?: string;
  getAvatar?: (player: RoomPlayer, isSelected: boolean) => React.ReactNode;
  getTitle?: (player: RoomPlayer) => React.ReactNode;
  getDescription?: (player: RoomPlayer) => React.ReactNode;
};

type PlayerSelectorSingleProps = PlayerSelectorCommonProps & {
  mode: 'single';
  selectedPlayerId: string | null;
  onSelect: (playerId: string) => void;
};

type PlayerSelectorMultiProps = PlayerSelectorCommonProps & {
  mode?: 'multi';
  selectedPlayerIds: string[];
  onSelectedPlayerIdsChange: (selectedPlayerIds: string[]) => void;
  targetCount: number;
};

export type PlayerSelectorProps =
  PlayerSelectorSingleProps | PlayerSelectorMultiProps;

const PlayerSelector: React.FC<PlayerSelectorProps> = (props) => {
  const {
    players,
    columns = 1,
    leadingOptions = [],
    selectedEmotion = 'surprised',
    className,
    getAvatar,
    getTitle = (player) => player.playerName,
    getDescription,
  } = props;
  const isAnimalStyleRoom = useAnimalStyleAvatars();

  const handleToggle = useCallback(
    (playerId: string) => {
      if (props.mode === 'single') {
        return;
      }

      if (props.selectedPlayerIds.includes(playerId)) {
        props.onSelectedPlayerIdsChange(
          props.selectedPlayerIds.filter((id) => id !== playerId)
        );
        return;
      }

      if (props.selectedPlayerIds.length >= props.targetCount) {
        return;
      }

      props.onSelectedPlayerIdsChange([...props.selectedPlayerIds, playerId]);
    },
    [props]
  );

  if (players.length === 0 && leadingOptions.length === 0) {
    return (
      <Empty className="min-h-44 py-8">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <UserRoundSearch />
          </EmptyMedia>
          <EmptyTitle>No players available</EmptyTitle>
        </EmptyHeader>
      </Empty>
    );
  }

  return (
    <ScrollArea
      className={cn(
        'min-h-0 rounded-lg [&_[data-slot=scroll-area-viewport]]:overscroll-y-contain',
        className
      )}
    >
      <FieldGroup
        className="grid gap-3"
        style={{
          gridTemplateColumns: `repeat(${Math.max(1, columns)}, minmax(0, 1fr))`,
        }}
      >
        {props.mode === 'single' ? (
          <RadioGroup
            value={props.selectedPlayerId ?? ''}
            onValueChange={props.onSelect}
            className="contents"
          >
            {leadingOptions.map((option) => {
              const isSelected = props.selectedPlayerId === option.id;
              const controlId = `player-selector-single-${option.id}`;

              return (
                <PlayerSelectorRow
                  key={option.id}
                  controlId={controlId}
                  isSelected={isSelected}
                  title={option.title}
                  description={option.description}
                  avatar={option.avatar}
                  onClick={() => {
                    if (isSelected) {
                      props.onSelect(option.id);
                    }
                  }}
                  control={<RadioGroupItem id={controlId} value={option.id} />}
                />
              );
            })}

            {players.map((player) => {
              const isSelected = props.selectedPlayerId === player.playerId;
              const description = getDescription?.(player);
              const controlId = `player-selector-single-${player.playerId}`;

              return (
                <PlayerSelectorRow
                  key={player.playerId}
                  controlId={controlId}
                  isSelected={isSelected}
                  title={getTitle(player)}
                  description={description}
                  onClick={() => {
                    if (isSelected) {
                      props.onSelect(player.playerId);
                    }
                  }}
                  avatar={
                    getAvatar ? (
                      getAvatar(player, isSelected)
                    ) : (
                      <UiAvatar className="size-12 overflow-visible bg-transparent after:hidden">
                        <GameAvatar
                          player={player}
                          size={48}
                          emotion={isSelected ? selectedEmotion : 'happy'}
                          // Keep selector row emotion deterministic (happy/surprised/angry)
                          // instead of switching to nervous from room waiting state.
                          allowWaitingEmotionOverride={false}
                          animalStyle={isAnimalStyleRoom}
                          enableGlow={isSelected}
                          glowType="static"
                          enableFloat={false}
                          enableBlinking={false}
                        />
                        <AvatarFallback className="sr-only">
                          {player.playerName.slice(0, 1)}
                        </AvatarFallback>
                      </UiAvatar>
                    )
                  }
                  control={
                    <RadioGroupItem id={controlId} value={player.playerId} />
                  }
                />
              );
            })}
          </RadioGroup>
        ) : (
          <>
            {leadingOptions.map((option) => {
              const isSelected = props.selectedPlayerIds.includes(option.id);
              const isDisabled =
                !isSelected &&
                props.selectedPlayerIds.length >= props.targetCount;
              const controlId = `player-selector-multi-${option.id}`;

              return (
                <PlayerSelectorRow
                  key={option.id}
                  controlId={controlId}
                  isSelected={isSelected}
                  isDisabled={isDisabled}
                  title={option.title}
                  description={option.description}
                  avatar={option.avatar}
                  control={
                    <Checkbox
                      id={controlId}
                      checked={isSelected}
                      disabled={isDisabled}
                      onCheckedChange={() => handleToggle(option.id)}
                    />
                  }
                />
              );
            })}

            {players.map((player) => {
              const isSelected = props.selectedPlayerIds.includes(
                player.playerId
              );
              const isDisabled =
                !isSelected &&
                props.selectedPlayerIds.length >= props.targetCount;
              const description = getDescription?.(player);
              const controlId = `player-selector-multi-${player.playerId}`;

              return (
                <PlayerSelectorRow
                  key={player.playerId}
                  controlId={controlId}
                  isSelected={isSelected}
                  isDisabled={isDisabled}
                  title={getTitle(player)}
                  description={description}
                  avatar={
                    getAvatar ? (
                      getAvatar(player, isSelected)
                    ) : (
                      <UiAvatar className="size-12 overflow-visible bg-transparent after:hidden">
                        <GameAvatar
                          player={player}
                          size={48}
                          emotion={isSelected ? selectedEmotion : 'happy'}
                          // Keep selector row emotion deterministic (happy/surprised/angry)
                          // instead of switching to nervous from room waiting state.
                          allowWaitingEmotionOverride={false}
                          animalStyle={isAnimalStyleRoom}
                          enableGlow={isSelected}
                          glowType="static"
                          enableFloat={false}
                          enableBlinking={false}
                        />
                        <AvatarFallback className="sr-only">
                          {player.playerName.slice(0, 1)}
                        </AvatarFallback>
                      </UiAvatar>
                    )
                  }
                  control={
                    <Checkbox
                      id={controlId}
                      checked={isSelected}
                      disabled={isDisabled}
                      onCheckedChange={() => handleToggle(player.playerId)}
                    />
                  }
                />
              );
            })}
          </>
        )}
      </FieldGroup>
    </ScrollArea>
  );
};

export default PlayerSelector;
